import invariant from 'invariant';
import set from 'lodash/set';

import { isEnabled } from 'Config';

import ConfigService from './ConfigService';
import createConfigService from './createConfigService';
import { REGISTERED_LEVER_VALUES } from './levers';
import {
  ConfigServiceLogFunction,
  ExtractLeverValueType,
  LeverConfigValue,
  LeverKey,
} from './types';
import { loadFromStorage, saveToStorage } from './utils';

let configServiceInstance: ConfigService | undefined;

export function initializeInstance(log?: ConfigServiceLogFunction): ConfigService {
  invariant(!configServiceInstance, 'Config Service already initialized!');

  configServiceInstance = createConfigService({ load: loadFromStorage, save: saveToStorage, log });

  if (__CLIENT__ && isEnabled('devTools')) {
    const invalidLever = (key: string | symbol) => {
      console.warn(`${String(key)} is not a registered lever`);
    };

    const LeversDevTool = new Proxy(
      Object.fromEntries(
        Object.entries(REGISTERED_LEVER_VALUES).map(([key, [, type]]) => [
          key,
          {
            getValue: () => configServiceInstance!._getValue(key as LeverKey)[0],
            setOverride(value: LeverConfigValue | undefined) {
              if (typeof value === type || typeof value === 'undefined') {
                configServiceInstance!.DEVTOOLS_setOverride(
                  key as LeverKey,
                  value as ExtractLeverValueType<LeverKey>
                );
              } else {
                console.error(`${key} requires override of type ${type}`);
              }
              return configServiceInstance!._getValue(key as LeverKey)[0];
            },
          },
        ])
      ),
      {
        get(target, key) {
          if (!(key in target)) {
            invalidLever(key);
            return undefined;
          }
          return target[key as LeverKey].getValue();
        },
        set(target, key, value) {
          if (!(key in target)) {
            invalidLever(key);
            return false;
          }
          target[key as LeverKey].setOverride(value);
          return true;
        },
        deleteProperty(target, key) {
          if (!(key in target)) {
            invalidLever(key);
            return false;
          }
          target[key as LeverKey].setOverride(undefined);
          return true;
        },
      }
    );

    set(window, ['__TINDER_DEVTOOLS__', 'Levers'], LeversDevTool);
  }

  return configServiceInstance;
}

export function getInstance() {
  invariant(configServiceInstance, 'Config Service not initialized!');
  return configServiceInstance;
}

export function setInstance(instance: ConfigService) {
  if (__TEST__) {
    configServiceInstance = instance;
  }
}
